import isEqual from 'lodash/isEqual';
import { isUndefined } from '../../../../predicates/type';
import { CustomObject } from '../../../../types';
import { getWindowParsedQueryParams } from '../../../../utils/url';
import { GeneralListResponse } from '../../types';
import { formatFilters, sanitizeValues } from '../../utils';
import {
	DEFAULT_REDUCER_VALUE,
	IS_LIST_PAGE_KEY,
	ListReducerAction,
	PERSISTED_LIST_RESPONSE_ITEMS_COUNT,
	STORAGE_PARTIAL_KEY,
} from './const';
import { getAsyncStorage, getSyncStorage, setSyncStorage, getStorage, setStorage } from './storage';
import { ListAction, ListProviderProps, ListReducerState, PersistedStorage } from './types';
import { DEFAULT_SKIP_FILTER_VALUES_KEYS, EXCLUDE_DEFAULT_FILTER_PARTIAL_KEYS } from '../../const';
import { stringifyPropsRecursively, transformKeysToLower } from '../../../../utils/object';
import { SavedFilterValue } from '../../components/ListSavedFilter';
import isObjectLike from 'lodash/isObjectLike';
import { getListProviderAnchorScrollData } from './anchorScrollUtils';
import { getActiveAnchorScrollPosition } from '../../../../components/PageAnchorScroll/utils';

export function getFilterListProviderQueryKey(moduleName?: string, userId?: number, listPage?: boolean): string {
	const listKey = [STORAGE_PARTIAL_KEY, `m:${moduleName}`, `uId:${userId}`].join('.');

	return listPage ? listKey.concat(`.${IS_LIST_PAGE_KEY}`) : listKey;
}

export function getListProviderQueryParams(params: Parameters<typeof formatFilters>[1] = {}): CustomObject {
	const queryParams = getWindowParsedQueryParams();

	return formatFilters(queryParams, {
		forceIncludeKeys: params.forceIncludeKeys,
		skipKeys: params.skipKeys,
		skipPartialKeys: params.skipPartialKeys,
	});
}

function isValidListResponse(data?: null | GeneralListResponse) {
	return (
		data !== null &&
		!isUndefined(data) &&
		!isUndefined(data.listItems) &&
		!isUndefined(data.filter) &&
		!isUndefined(data.filter.values)
	);
}

function isEqualFilterValues(filterValues1: CustomObject, filterValues2: CustomObject) {
	const lowerCaseFilterValues1 = transformKeysToLower(filterValues1);
	const stringifiedFilterValues1 = stringifyPropsRecursively(lowerCaseFilterValues1);
	const lowerCaseFilterValues2 = transformKeysToLower(filterValues2);
	const stringifiedFilterValues2 = stringifyPropsRecursively(lowerCaseFilterValues2);

	Object.keys(stringifiedFilterValues1).forEach((key) => {
		const value = stringifiedFilterValues1[key];

		if (Array.isArray(value) && value.length === 1) {
			stringifiedFilterValues1[key] = String(value[0]);
		}
	});

	Object.keys(stringifiedFilterValues2).forEach((key) => {
		const value = stringifiedFilterValues2[key];

		if (Array.isArray(value) && value.length === 1) {
			stringifiedFilterValues2[key] = String(value[0]);
		}
	});

	return isEqual(stringifiedFilterValues1, stringifiedFilterValues2);
}

export function getMatchedSlotItem({
	storage,
	filterValues: filterValuesFromProps,
	options = {},
}: {
	storage?: PersistedStorage | null;
	filterValues: CustomObject;
	options?: {
		forceIncludeKeys?: string[];
		skipKeys?: string[];
		skipPartialKeys?: string[];
	};
}): GeneralListResponse | null {
	if (isUndefined(storage) || storage === null) {
		return null;
	}

	const filterValues = formatFiltersForStorageComparison(filterValuesFromProps, {
		forceIncludeKeys: options.forceIncludeKeys,
		skipKeys: options.skipKeys,
	});
	const matchSlotItem = (slotItem: GeneralListResponse) => {
		if (!isValidListResponse(slotItem)) {
			return false;
		}

		const slotItemFilterValues = formatFiltersForStorageComparison(slotItem.filter.values, {
			forceIncludeKeys: options.forceIncludeKeys,
			skipKeys: options.skipKeys,
		});

		return isEqualFilterValues(filterValues, slotItemFilterValues);
	};

	if (matchSlotItem(storage.initial)) {
		return storage.initial;
	}

	if (!Array.isArray(storage.recent)) {
		return null;
	}

	const recentSlotItem = storage.recent.find((slotItem) => {
		return matchSlotItem(slotItem);
	});

	return recentSlotItem || null;
}

export function getSyncDefaultState({
	requestKey,
	forceIncludeKeys,
	skipKeys,
}: {
	requestKey: string;
	forceIncludeKeys: string[];
	skipKeys?: string[];
}) {
	const storage = getSyncStorage(requestKey);
	const queryParams = getWindowParsedQueryParams();

	return getMatchedSlotItem({
		storage,
		filterValues: queryParams,
		options: {
			forceIncludeKeys,
			skipKeys,
		},
	});
}

export async function getAsyncDefaultState({
	requestKey,
	forceIncludeKeys,
	skipKeys,
}: {
	requestKey: string;
	forceIncludeKeys: string[];
	skipKeys?: string[];
}) {
	const queryParams = getWindowParsedQueryParams();

	try {
		const decryptedStoreValue = await getAsyncStorage(requestKey);

		if (decryptedStoreValue !== null) {
			setSyncStorage(requestKey, decryptedStoreValue);
		}

		return getMatchedSlotItem({
			storage: decryptedStoreValue,
			filterValues: queryParams,
			options: {
				forceIncludeKeys,
				skipKeys,
			},
		});
	} catch (e) {
		return null;
	}
}

export function listProviderReducer(state: ListReducerState, action: ListAction): ListReducerState {
	switch (action.type) {
		case ListReducerAction.START_FETCHING: {
			return {
				...state,
				isAvailable: false,
				isLoading: !!action.payload.firstRequest,
				isFilterSubmitted: !!action.payload.isFilterSubmitted,
				isPaginationSubmitted: !!action.payload.isPaginationSubmitted,
			};
		}
		case ListReducerAction.TOGGLE_AVAILABILITY: {
			return {
				...state,
				isAvailable: action.payload,
			};
		}
		case ListReducerAction.RESET_SAVED_FILTER_STATE: {
			if (state.data.filter?.values.savedFilter === null || state.data.filter?.metadata.savedFilter === null) {
				return state;
			}

			return {
				...state,
				lastFilterSubmitTime: new Date(),
				isLoading: false,
				isAvailable: true,
				data: {
					...state.data,
					filter: {
						values: {
							...state.data.filter!.values,
							// @ts-ignore
							savedFilter: {
								...state.data.filter!.values.savedFilter,
								id: 0,
								libraryFilterId: 0,
							},
						},
						metadata: {
							...state.data.filter!.metadata,
							savedFilter: {
								...state.data.filter!.metadata.savedFilter,
								id: {
									dropdownDisplayValue: [],
								},
							},
						},
					},
				},
			};
		}
		case ListReducerAction.ACTIVATE_SAVED_FILTER: {
			if (state.data.filter?.values.savedFilter === null || state.data.filter?.metadata.savedFilter === null) {
				return state;
			}

			const { payload } = action;
			return {
				...state,
				lastFilterSubmitTime: new Date(),
				isLoading: false,
				isAvailable: true,
				data: {
					...state.data,
					filter: {
						values: {
							...state.data.filter!.values,
							// @ts-ignore
							savedFilter: {
								...state.data.filter!.values.savedFilter,
								id: payload.id,
								libraryFilterId: payload.libraryFilterId,
								_lastChanged: new Date(),
							},
						},
						metadata: {
							...state.data.filter!.metadata,
							...payload.metadata,
						},
					},
				},
			};
		}
		case ListReducerAction.SET_ITEMS_LIST_COUNT: {
			return {
				...state,
				data: {
					...state.data,
					listItemsCount: action.payload,
				},
			};
		}
		case ListReducerAction.SET_ERROR: {
			return {
				...state,
				isAvailable: true,
				isFetched: false,
				isLoading: false,
				error: action.payload,
			};
		}
		case ListReducerAction.SET_RESPONSE: {
			const newState = {
				...state,
				isAvailable: true,
				isFetched: true,
				isLoading: false,
				error: null,
				lastFilterSubmitTime: new Date(),
				data: action.payload,
			};

			return {
				...newState,
				canFetchMore:
					newState.data.listItems.length > 0 && newState.data.listItems.length % state.defaultTake === 0,
			};
		}
		case ListReducerAction.SET_RESPONSE_FILTER_DATA: {
			return {
				...state,
				data: {
					...state.data,
					...action.payload,
				},
			};
		}
		case ListReducerAction.SET_LAST_FILTER_COMPONENT_FETCHED_TIME: {
			return {
				...state,
				lastFilterComponentFetchedTime: {
					...state.lastFilterComponentFetchedTime,
					[action.payload]: state.lastFilterSubmitTime,
				},
			};
		}
		case ListReducerAction.SET_INITIAL_DATA: {
			if (state.isFetched && !action.payload.force) {
				return state;
			}

			return {
				...state,
				data: action.payload.data,
				isAvailable: false,
				hasInitialData: true,
			};
		}
		case ListReducerAction.APPEND_LIST_ITEMS: {
			return {
				...state,
				isAvailable: true,
				data: {
					...state.data,
					listItems: [...state.data.listItems, ...action.payload],
				},
				canFetchMore: action.payload.length > 0 && action.payload.length % state.defaultTake === 0,
			};
		}
		case ListReducerAction.SET_LIST: {
			const setList = action.payload;
			const listItems = setList(state.data.listItems);

			return {
				...state,
				data: {
					...state.data,
					listItems,
				},
			};
		}

		default:
			return state;
	}
}

function formatFiltersForStorageComparison(
	filterValues: CustomObject,
	options: Omit<
		Parameters<typeof formatFilters>[1],
		'isSelectedSavedFilter' | 'includePagination' | 'skipPartialKeys'
	> = {
		skipKeys: [],
	},
) {
	if (isObjectLike(filterValues.savedFilter)) {
		const savedFilter = filterValues.savedFilter as SavedFilterValue;

		if (savedFilter.id === -1) {
			return {};
		}
	}

	return formatFilters(filterValues, {
		forceIncludeKeys: options.forceIncludeKeys,
		skipKeys: [...(options.skipKeys ?? []), ...DEFAULT_SKIP_FILTER_VALUES_KEYS],
		skipPartialKeys: EXCLUDE_DEFAULT_FILTER_PARTIAL_KEYS,
		includePagination: false,
		isSelectedSavedFilter: false,
	});
}

export function getInvalidatedStorageSlots(
	listResponse: GeneralListResponse,
	persistedStorage: PersistedStorage | null,
	options: {
		persistColumnsOnly?: boolean;
		skipCacheQueryParamKeys?: string[];
		includeQueryKeys?: string[];
		firstEmptyValuesSubmit?: boolean;
	} = {},
): PersistedStorage {
	if (options.persistColumnsOnly) {
		return {
			initial: listResponse,
		};
	}

	const responseFilterValues = formatFiltersForStorageComparison(listResponse.filter.values, {
		forceIncludeKeys: options.includeQueryKeys,
		skipKeys: options.skipCacheQueryParamKeys,
	});
	const hasPrevStorageSlots = persistedStorage !== null;
	const normalizedPersistedStorage: PersistedStorage = hasPrevStorageSlots ? persistedStorage! : {};
	const isEqualSlotItemFilterValues = (slotItemFilterValues: CustomObject) => {
		const initialSlotFilterValues = formatFiltersForStorageComparison(slotItemFilterValues, {
			forceIncludeKeys: options.includeQueryKeys,
			skipKeys: options.skipCacheQueryParamKeys,
		});

		return isEqualFilterValues(responseFilterValues, initialSlotFilterValues);
	};

	/*
		Case: When a user submits a filter with empty values for the first time
		Case: When a user submits a filter with empty values, but we receive some preselected data from the backend.
	*/
	if (options.firstEmptyValuesSubmit || Object.keys(responseFilterValues).length === 0) {
		return {
			...normalizedPersistedStorage,
			initial: listResponse,
		};
	}

	/*
		Case: When a user creates a default saved filter with preselected values and then navigates to a certain module,
		it creates an initial slot. However, if the user simply reloads the page, we need to maintain consistency within
		the initial snapshot and also cache it, treating it like an initial slot.
	*/
	if (
		!isUndefined(normalizedPersistedStorage.initial) &&
		isEqualSlotItemFilterValues(normalizedPersistedStorage.initial.filter.values)
	) {
		return {
			...normalizedPersistedStorage,
			initial: listResponse,
		};
	}

	if (!Array.isArray(normalizedPersistedStorage.recent) || normalizedPersistedStorage.recent.length === 0) {
		return {
			...normalizedPersistedStorage,
			recent: [listResponse],
		};
	}

	let hasMatchedSlot = false;
	let recentSlot = normalizedPersistedStorage.recent.map((slot) => {
		if (hasMatchedSlot) {
			return slot;
		}

		if (isEqualSlotItemFilterValues(slot.filter.values)) {
			hasMatchedSlot = true;

			return listResponse;
		}

		return slot;
	});

	if (!hasMatchedSlot) {
		recentSlot = [listResponse, ...recentSlot];
	}

	return {
		...normalizedPersistedStorage,
		recent: recentSlot.slice(0, PERSISTED_LIST_RESPONSE_ITEMS_COUNT),
	};
}

export async function invalidateStorage(
	requestKey: string,
	listResponse: GeneralListResponse,
	options: {
		persistData?: boolean;
		skipCacheQueryParamKeys?: string[];
		includeQueryKeys?: string[];
		firstEmptyValuesSubmit?: boolean;
	} = {},
) {
	const decryptedStorageValue = await getStorage(requestKey);
	const storageValue = getInvalidatedStorageSlots(listResponse, decryptedStorageValue, {
		persistColumnsOnly: !options.persistData,
		skipCacheQueryParamKeys: options.skipCacheQueryParamKeys,
		includeQueryKeys: options.includeQueryKeys,
		firstEmptyValuesSubmit: options.firstEmptyValuesSubmit,
	});

	setStorage(requestKey, storageValue);
}

export function isEmptySanitizedFilterValues(
	filters: CustomObject,
	{
		forceIncludeKeys,
		skipKeys,
	}: {
		forceIncludeKeys?: string[];
		skipKeys?: string[];
	} = {},
) {
	const sanitizedFilters = sanitizeValues(filters, {
		skipKeys: [...DEFAULT_SKIP_FILTER_VALUES_KEYS, ...skipKeys],
		forceIncludeKeys,
		skipPartialKeys: EXCLUDE_DEFAULT_FILTER_PARTIAL_KEYS,
	});

	return Object.keys(sanitizedFilters).length === 0;
}

export function getDefaultListProviderState({
	formName,
	defaultTake,
	shouldPersist,
	requestKey,
	includeQueryKeys,
	skipCacheQueryParamKeys,
	listPage,
	hasInitialRequest,
}: Pick<
	ListProviderProps,
	'formName' | 'defaultTake' | 'includeQueryKeys' | 'skipCacheQueryParamKeys' | 'listPage' | 'hasInitialRequest'
> & {
	shouldPersist: boolean;
	requestKey: string;
}) {
	return () => {
		const listAnchorScrollData = getListProviderAnchorScrollData();
		const anchorScrollPosition = getActiveAnchorScrollPosition();
		const hasValidAnchorState = listPage && anchorScrollPosition !== null && listAnchorScrollData !== null;
		let defaultValue = {
			...DEFAULT_REDUCER_VALUE,
			isAvailable: !hasInitialRequest,
			formName,
			defaultTake,
		};

		if (hasValidAnchorState) {
			defaultValue = {
				...defaultValue,
				data: {
					...defaultValue,
					...listAnchorScrollData,
				},
				hasInitialData: true,
			};
		}

		if (!shouldPersist) {
			return defaultValue;
		}

		const syncDefaultState = getSyncDefaultState({
			requestKey,
			forceIncludeKeys: includeQueryKeys,
			skipKeys: skipCacheQueryParamKeys,
		});

		if (syncDefaultState === null) {
			return defaultValue;
		}

		const listItems = hasValidAnchorState ? defaultValue.data.listItems : syncDefaultState.listItems;

		return {
			...defaultValue,
			data: {
				...syncDefaultState,
				listItems,
			},
			hasInitialData: true,
		};
	};
}
